import { useEffect, useState, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Spinner } from '../components/ui/spinner';
import { MapLocationButton } from '../components/MapLocationButton';
import { Waves, Search, MapPin, Flag, Droplets, Car, ShowerHead, Utensils, LifeBuoy, ExternalLink } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tenerife center
const centerPosition = [28.2916, -16.6291];

// Beach icon
const beachIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const goldenBeachIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-gold.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const createClusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  return L.divIcon({
    html: `<div class="flex items-center justify-center w-10 h-10 rounded-full bg-cyan-500 text-white font-bold text-sm shadow-lg border-2 border-white">${count}</div>`,
    className: 'custom-cluster-icon',
    iconSize: L.point(40, 40),
  });
};

// Service icons mapping
const serviceIcons = {
  duchas: ShowerHead,
  aseos: Droplets,
  socorrista: LifeBuoy,
  restaurantes: Utensils,
  aparcamiento: Car,
  chiringuito: Utensils,
  deportes_acuaticos: Waves,
};

const serviceLabels = {
  es: {
    duchas: 'Duchas',
    aseos: 'Aseos',
    socorrista: 'Socorrista',
    restaurantes: 'Restaurantes',
    aparcamiento: 'Aparcamiento',
    chiringuito: 'Chiringuito',
    deportes_acuaticos: 'Deportes acuáticos',
  },
  en: {
    duchas: 'Showers',
    aseos: 'Toilets',
    socorrista: 'Lifeguard',
    restaurantes: 'Restaurants',
    aparcamiento: 'Parking',
    chiringuito: 'Beach bar',
    deportes_acuaticos: 'Water sports',
  }
};

export function BeachesPage() {
  const { language } = useLanguage();
  const [beaches, setBeaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [blueFlag, setBlueFlag] = useState('all');

  const t = {
    title: language === 'es' ? 'Playas' : 'Beaches',
    subtitle: language === 'es' ? 'Playas de Tenerife' : 'Beaches of Tenerife',
    description: language === 'es' 
      ? 'Explora las principales playas de Tenerife, desde calas naturales hasta zonas de baño urbanas. Consulta su ubicación, características y servicios disponibles para planificar tu visita de forma sencilla y segura.'
      : 'Explore the main beaches of Tenerife, from natural coves to urban bathing areas. Check their location, features and available services to plan your visit easily and safely.',
    searchPlaceholder: language === 'es' ? 'Buscar playa...' : 'Search beach...',
    beaches: language === 'es' ? 'playas' : 'beaches',
    loading: language === 'es' ? 'Cargando playas...' : 'Loading beaches...',
    allTypes: language === 'es' ? 'Todos los tipos' : 'All types',
    filterByType: language === 'es' ? 'Filtrar por tipo de arena' : 'Filter by sand type',
    blueFlag: language === 'es' ? 'Bandera Azul' : 'Blue Flag',
    allBeaches: language === 'es' ? 'Todas' : 'All',
    onlyBlueFlag: language === 'es' ? 'Solo Bandera Azul' : 'Blue Flag only',
    services: language === 'es' ? 'Servicios' : 'Services',
    openInMaps: language === 'es' ? 'Abrir en Google Maps' : 'Open in Google Maps',
    showing: language === 'es' ? 'Mostrando' : 'Showing',
    of: language === 'es' ? 'de' : 'of',
    results: language === 'es' ? 'resultados' : 'results',
    dataSource: language === 'es' ? 'Fuente: Elaboración propia con datos públicos' : 'Source: Own elaboration with public data',
  };

  useEffect(() => {
    const fetchBeaches = async () => {
      try {
        const response = await axios.get(`${API}/beaches`);
        setBeaches(response.data || []);
      } catch (error) {
        console.error('Error fetching beaches:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBeaches();
  }, []);

  // Types grouped as "Arena negra y callaos"
  const ARENA_NEGRA_TYPES = ['Arena negra', 'Arena y rocas', 'Callaos', 'Rocas', 'Rocas y callaos'];

  // Simplified filter categories
  const sandTypes = ['Arena dorada', 'Arena negra y callaos'];

  // Filter beaches
  const filteredBeaches = useMemo(() => {
    let filtered = beaches;

    if (typeFilter !== 'all') {
      if (typeFilter === 'Arena negra y callaos') {
        filtered = filtered.filter(b => ARENA_NEGRA_TYPES.includes(b.tipo));
      } else {
        filtered = filtered.filter(b => b.tipo === typeFilter);
      }
    }

    if (blueFlag === 'yes') {
      filtered = filtered.filter(b => b.bandera_azul);
    }

    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(b =>
        (b.nombre || '').toLowerCase().includes(searchLower) ||
        (b.municipio || '').toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [beaches, typeFilter, blueFlag, search]);

  // Type colors
  const typeColors = {
    'Arena dorada': '#f59e0b',
    'Arena negra': '#374151',
    'Callaos': '#6b7280',
    'Rocas': '#78716c',
    'Rocas y callaos': '#57534e',
    'Arena y rocas': '#92400e',
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Waves className="w-5 h-5 text-cyan-500" />
          <span className="text-sm font-medium text-cyan-600 uppercase tracking-wider">
            {t.title}
          </span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#252a34] font-['Outfit'] mb-2">
          {t.subtitle}
        </h1>
        <p className="text-base text-[#252a34]/70 mb-2">{t.description}</p>
        <p className="text-sm text-[#252a34]/50">{t.dataSource}</p>
      </div>

      {/* Stats */}
      <div className="mb-6 flex items-center gap-4 flex-wrap">
        <div className="bg-white rounded-lg px-4 py-2 shadow-sm border border-slate-200">
          <span className="text-2xl font-bold text-cyan-500">{beaches.length}</span>
          <span className="text-gray-600 ml-2">{t.beaches}</span>
        </div>
        <div className="bg-white rounded-lg px-4 py-2 shadow-sm border border-slate-200 flex items-center gap-2">
          <Flag className="w-4 h-4 text-blue-500" />
          <span className="text-lg font-bold text-blue-500">{beaches.filter(b => b.bandera_azul).length}</span>
          <span className="text-gray-600">{t.blueFlag}</span>
        </div>
      </div>

      {/* Filters */}
      {!loading && (
        <div className="space-y-4 mb-4">
          {/* Sand type filter */}
          <div>
            <p className="text-sm text-[#252a34]/60 mb-2">{t.filterByType}:</p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setTypeFilter('all')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  typeFilter === 'all'
                    ? 'bg-cyan-500 text-white'
                    : 'bg-white text-[#252a34] border border-slate-200 hover:border-cyan-400'
                }`}
              >
                {t.allTypes} ({beaches.length})
              </button>
              {sandTypes.map((tipo) => {
                const count = tipo === 'Arena negra y callaos' 
                  ? beaches.filter(b => ARENA_NEGRA_TYPES.includes(b.tipo)).length
                  : beaches.filter(b => b.tipo === tipo).length;
                const color = tipo === 'Arena dorada' ? '#f59e0b' : '#374151';
                const isActive = typeFilter === tipo;
                return (
                  <button
                    key={tipo}
                    onClick={() => setTypeFilter(tipo)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      isActive
                        ? 'text-white shadow-md'
                        : 'bg-white text-[#252a34] border border-slate-200 hover:shadow-sm'
                    }`}
                    style={isActive ? { backgroundColor: color } : {}}
                  >
                    {tipo} ({count})
                  </button>
                );
              })}
            </div>
          </div>

          {/* Blue flag filter */}
          <div className="flex items-center gap-4">
            <div className="flex gap-2">
              <button
                onClick={() => setBlueFlag('all')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1 ${
                  blueFlag === 'all'
                    ? 'bg-slate-600 text-white'
                    : 'bg-white text-[#252a34] border border-slate-200 hover:border-slate-400'
                }`}
              >
                {t.allBeaches}
              </button>
              <button
                onClick={() => setBlueFlag('yes')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1 ${
                  blueFlag === 'yes'
                    ? 'bg-blue-500 text-white'
                    : 'bg-white text-[#252a34] border border-slate-200 hover:border-blue-400'
                }`}
              >
                <Flag className="w-3 h-3" />
                {t.onlyBlueFlag}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      {!loading && (
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#252a34]/40" />
            <Input
              type="text"
              placeholder={t.searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-12 bg-white border-slate-200"
            />
          </div>
          <div className="text-sm text-[#252a34]/60 flex items-center gap-2">
            {t.showing} <strong className="text-cyan-500">{filteredBeaches.length}</strong> {t.of} {beaches.length} {t.results}
          </div>
        </div>
      )}

      {/* Map */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="h-[500px] rounded-lg overflow-hidden relative">
            {loading ? (
              <div className="flex items-center justify-center h-full bg-slate-50">
                <Spinner className="mr-2" />
                {t.loading}
              </div>
            ) : (
              <MapContainer center={centerPosition} zoom={9} style={{ height: '100%', width: '100%' }} className="z-0">
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapLocationButton language={language} />
                <MarkerClusterGroup 
                  chunkedLoading 
                  iconCreateFunction={createClusterIcon} 
                  maxClusterRadius={50}
                >
                  {filteredBeaches.map((beach, idx) => {
                    const isGolden = beach.tipo?.toLowerCase().includes('dorada');
                    return (
                      <Marker 
                        key={idx} 
                        position={[beach.lat, beach.lon]} 
                        icon={isGolden ? goldenBeachIcon : beachIcon}
                      >
                        <Popup>
                          <div className="font-['Outfit'] min-w-[250px] max-w-[300px]">
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <h3 className="font-bold text-[#252a34] text-sm">{beach.nombre}</h3>
                              {beach.bandera_azul && (
                                <Badge className="bg-blue-500 text-white text-xs flex items-center gap-1">
                                  <Flag className="w-3 h-3" />
                                  Bandera Azul
                                </Badge>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-2 mb-2">
                              <Badge 
                                className="text-xs text-white"
                                style={{ backgroundColor: typeColors[beach.tipo] || '#6b7280' }}
                              >
                                {beach.tipo}
                              </Badge>
                              <span className="text-xs text-[#252a34]/60 flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {beach.municipio}
                              </span>
                            </div>
                            
                            {beach.descripcion && (
                              <p className="text-xs text-[#252a34]/70 mb-3">{beach.descripcion}</p>
                            )}
                            
                            {beach.servicios && beach.servicios.length > 0 && (
                              <div className="mb-3">
                                <p className="text-xs font-semibold text-[#252a34]/80 mb-1">{t.services}:</p>
                                <div className="flex flex-wrap gap-1">
                                  {beach.servicios.map((servicio, i) => {
                                    const Icon = serviceIcons[servicio];
                                    const label = serviceLabels[language]?.[servicio] || servicio;
                                    return (
                                      <span key={i} className="text-xs bg-slate-100 px-2 py-0.5 rounded flex items-center gap-1">
                                        {Icon && <Icon className="w-3 h-3" />}
                                        {label}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${beach.lat},${beach.lon}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-cyan-600 hover:text-cyan-800 font-medium"
                            >
                              <ExternalLink className="w-3 h-3" />
                              {t.openInMaps}
                            </a>
                          </div>
                        </Popup>
                      </Marker>
                    );
                  })}
                </MarkerClusterGroup>
              </MapContainer>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
